import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FamilySelector, useFamilySelection } from '../common';

interface NoteData {
  title: string;
  text: string;
}

interface NoteFormDialogProps {
  open: boolean;
  onClose: () => void;
  // onSave accepts familyIds array for multi-family support
  onSave: (data: NoteData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  note?: { id: string; family_id?: string; data?: { data?: NoteData } } | null;
}

export const NoteFormDialog: React.FC<NoteFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  note,
}) => {
  const isEditing = !!note;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection(note?.family_id ? [note.family_id] : undefined);

  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (note) {
        const data = (note.data?.data || {}) as NoteData;
        setTitle(data.title || '');
        setText(data.text || '');
      } else {
        setTitle('');
        setText('');
      }
      setError(null);
    }
  }, [open, note]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (selectedFamilyIds.length === 0) {
      setError('Please select at least one family');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(
        {
          title: title.trim(),
          text: text.trim(),
        },
        selectedFamilyIds
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Note' : 'New Note'}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          required
          autoFocus
          sx={{ mb: 2 }}
        />

        {/* Family Selector - only shows if user belongs to multiple families */}
        {showFamilySelector && !isEditing && (
          <Box sx={{ mb: 2 }}>
            <FamilySelector
              value={selectedFamilyIds}
              onChange={setSelectedFamilyIds}
            />
          </Box>
        )}

        <TextField
          label="Content"
          value={text}
          onChange={(e) => setText(e.target.value)}
          fullWidth
          multiline
          rows={8}
          placeholder="Write your note..."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button
            onClick={handleDelete}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={saving}
            sx={{ mr: 'auto' }}
          >
            Delete
          </Button>
        )}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteFormDialog;
